import { gql } from '@apollo/client';
import EnergySavingPage from '~/components/EnergySaving/EnergySavingPage';
import LongReadPage from '~/components/LongRead/LongRead';
import type {
  getEnergySavingOrLongReadBySlug,
  getEnergySavingOrLongReadBySlugVariables,
  getEnergySavingOrLongReadBySlug_allEnergySavingPages,
  getEnergySavingOrLongReadBySlug_allLongReads,
} from '~/types/generated/cms/getEnergySavingOrLongReadBySlug';
import type { getEnergySavingSlugs } from '~/types/generated/cms/getEnergySavingSlugs';
import {
  EnergySavingPageFragment,
  EnergySavingPageSlugsFragment,
  LongReadPageFragment,
} from 'src/datoCMS/fragments/woningVerduurzamenFragments';
import { getDatoClient } from 'src/datoCMS/datoCmsClient';
import {
  allLongReadPageSlugs,
  constructWoningVerduurzamenPageSlugs,
} from '../../product/[...slug]';
import type { ReactNode } from 'react';
import type { GetStaticPaths, GetStaticProps } from 'next';

const client = getDatoClient();

const GET_ENERGY_SAVING_OR_LONG_READ_BY_SLUG = gql`
  ${LongReadPageFragment}
  ${EnergySavingPageFragment}
  query getEnergySavingOrLongReadBySlug($slug: String!) {
    allEnergySavingPages(filter: { slug: { eq: $slug } }) {
      ...EnergySavingPageFragment
    }

    allLongReads(filter: { slug: { eq: $slug } }) {
      ...LongReadPageFragment
    }
  }
`;

const GET_ENERGY_SAVING_SLUGS = gql`
  ${EnergySavingPageSlugsFragment}
  query getEnergySavingSlugs {
    allEnergySavingPages {
      ...EnergySavingPageSlugsFragment
    }
  }
`;

export const allEnergySavingPageSlugs = async () => {
  const { data } = await client.query<getEnergySavingSlugs>({
    query: GET_ENERGY_SAVING_SLUGS,
  });

  return data.allEnergySavingPages;
};

interface PageProps {
  page:
    | getEnergySavingOrLongReadBySlug_allLongReads
    | getEnergySavingOrLongReadBySlug_allEnergySavingPages;
}

const EnergyBesparenArticle: React.FC<PageProps> = ({ page }) => {
  const renderedPage: Record<'LongReadRecord' | 'EnergySavingPageRecord', ReactNode> = {
    LongReadRecord: <LongReadPage page={page as getEnergySavingOrLongReadBySlug_allLongReads} />,
    EnergySavingPageRecord: (
      <EnergySavingPage page={page as getEnergySavingOrLongReadBySlug_allEnergySavingPages} />
    ),
  };

  return <>{renderedPage[page.__typename]}</>;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const energySavingPageSlugs = await allEnergySavingPageSlugs();
  const longReadPageSlugs = await allLongReadPageSlugs();

  const pageSlugs = constructWoningVerduurzamenPageSlugs(
    [...longReadPageSlugs],
    [...energySavingPageSlugs],
  );

  return {
    paths: pageSlugs.map(p => ({ params: { slug: p } })),
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<PageProps> = async ({ preview, params }) => {
  const datoClient = getDatoClient(preview);
  if (!params || !params.slug?.length) return { notFound: true };

  const { data } = await datoClient.query<
    getEnergySavingOrLongReadBySlug,
    getEnergySavingOrLongReadBySlugVariables
  >({
    query: GET_ENERGY_SAVING_OR_LONG_READ_BY_SLUG,
    variables: { slug: params.slug![params.slug!.length - 1] as string },
  });

  const renderedPageData = data.allLongReads[0] || data.allEnergySavingPages[0];
  if (!renderedPageData) {
    return { notFound: true };
  }

  return {
    props: {
      page: renderedPageData,
    },
    revalidate: 60,
  };
};

export default EnergyBesparenArticle;
